import {
  createSlice,
  createEntityAdapter,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import format from 'date-fns/format';

import {
  GET_DISPUTES,
  GET_DISPUTES_ADYEN_REASONS,
  GET_DISPUTES_BY_ID,
  GET_STRIPE_DISPUTE,
  POST_EXPORT_DISPUTES,
} from 'services/api';
import {
  DISPUTE,
  DISPUTE_ALL,
  DISPUTE_BY_ID,
  DISPUTE_SEARCH,
  DISPUTE_EXPORT,
  DISPUTE_REASONS,
  DISPUTE_STRIPE,
} from './types';
import { last180Days } from 'components/DateRangePicker/ranges';
import { DATE_FOR_API } from 'utils/dateFormat';
import { AMOUNT_FILTERS } from 'components/PaymentFilter/defaultFilters';
import { priceForAPI } from 'utils/price';
import { rejectValue, flatDispute } from 'redux/helpers';
import {
  NOTIFICATION_RECEIVED,
  INFORMATION_REQUESTED,
  FIRST_CHARGEBACK,
  DEFENSE_DECLINED,
  DEFENSE_ACCEPTED,
  WON,
  LOST,
  CHARGE_REFUNDED,
  UNDER_REVIEW,
  NEEDS_RESPONSE,
  CLOSED,
} from 'pages/Disputes/helpers';
import { showSuccessMessage } from 'redux/slices/notification';

const disputeAdapter = createEntityAdapter();

export const initialState = disputeAdapter.getInitialState({
  isLoading: false,
  detailsIsLoading: false,
  pagination: {
    total: 1,
    total_pages: 1,
    current_page: 1,
    per_page: 25,
    count: 1,
  },
  params: {
    per_page: 25,
    from: format(last180Days.startDate, DATE_FOR_API),
    to: format(last180Days.endDate, DATE_FOR_API),
    amount_filter: AMOUNT_FILTERS[1],
    amount: null,
    statusCheckbox: true,
    status: `${NOTIFICATION_RECEIVED},${INFORMATION_REQUESTED},${FIRST_CHARGEBACK},${DEFENSE_DECLINED},${DEFENSE_ACCEPTED},${WON},${LOST},${CHARGE_REFUNDED},${UNDER_REVIEW},${NEEDS_RESPONSE},${CLOSED}`,
  },
  search: {
    term: '',
    filter: undefined,
    submitted: false,
  },
});

export const getDisputes = createAsyncThunk(
  DISPUTE_ALL,
  async (_, { rejectWithValue, getState }) => {
    try {
      const companyId = getState().company.current;
      const { status, statusCheckbox, amount, amount_filter, ..._params } =
        getState().dispute.params;

      let params = { ..._params };

      if (statusCheckbox && status) {
        params = { ...params, status };
      }
      if (!!amount) {
        const amountForAPI = priceForAPI(amount);
        params = { ...params, [amount_filter.value]: amountForAPI };
      }

      const { data, meta } = await GET_DISPUTES({
        ...params,
        company_id: companyId,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };
      const _data = data.map(flatDispute);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(
        rejectValue({ ...error.response, skipNotification: true })
      );
    }
  }
);

export const getDisputesById = createAsyncThunk(
  DISPUTE_BY_ID,
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await GET_DISPUTES_BY_ID(id);

      return flatDispute(data);
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getDisputeReasons = createAsyncThunk(
  DISPUTE_REASONS,
  async (id, { rejectWithValue }) => {
    try {
      const data = await GET_DISPUTES_ADYEN_REASONS(id);

      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getStripeDispute = createAsyncThunk(
  DISPUTE_STRIPE,
  async (id, { rejectWithValue }) => {
    try {
      const data = await GET_STRIPE_DISPUTE(id);

      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const searchDisputes = createAsyncThunk(
  DISPUTE_SEARCH,
  async (_, { rejectWithValue, getState }) => {
    try {
      const companyId = getState().company.current;
      const { term, filter } = getState().dispute.search;

      if (!(term && filter?.value)) {
        return;
      }

      const { data, meta } = await GET_DISPUTES({
        [filter.value]: term,
        company_id: companyId,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };

      const _data = data.map(flatDispute);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const exportDisputes = createAsyncThunk(
  DISPUTE_EXPORT,
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const email = getState().auth.user.email;
      const { term, filter, submitted } = getState().dispute.search;

      let params = {};

      if (term && filter && submitted) {
        params = { [filter.value]: term };
      } else {
        const {
          status,
          statusCheckbox,
          amount,
          amount_filter,
          per_page,
          page,
          ..._params
        } = getState().dispute.params;

        params = { ..._params };
        if (statusCheckbox && status) {
          params = { ...params, status };
        }
        if (!!amount) {
          const amountForAPI = priceForAPI(amount);
          params = { ...params, [amount_filter.value]: amountForAPI };
        }
      }
      await POST_EXPORT_DISPUTES(params);
      dispatch(
        showSuccessMessage(
          `Check your email account ${email} for access to the file.`
        )
      );
    } catch (error) {
      return rejectWithValue(
        rejectValue({ ...error.response, skipNotification: true })
      );
    }
  }
);

const disputeSlice = createSlice({
  name: DISPUTE,
  initialState,
  reducers: {
    setParams: (state, { payload }) => {
      state.params = { ...state.params, ...payload };
    },
    setSearchTerm: (state, { payload }) => {
      state.search.term = payload;
    },
    setSearchFilter: (state, { payload }) => {
      state.search.filter = payload;
    },
    resetSearch: (state) => {
      state.search = initialState.search;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDisputes.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = false;
      })
      .addCase(getDisputes.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getDisputes.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        disputeAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addCase(getDisputesById.pending, (state) => {
        state.detailsIsLoading = true;
      })
      .addCase(getDisputesById.rejected, (state) => {
        state.detailsIsLoading = false;
      })
      .addCase(getDisputesById.fulfilled, (state, { payload }) => {
        state.detailsIsLoading = false;
        disputeAdapter.upsertOne(state, payload);
      })
      .addCase(searchDisputes.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = true;
      })
      .addCase(searchDisputes.rejected, (state) => {
        state.isLoading = false;
        state.search.submitted = false;
      })
      .addCase(searchDisputes.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        disputeAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addCase(getDisputeReasons.fulfilled, (state, { payload }) => {
        state.reasons = payload;
      })
      .addCase(getStripeDispute.fulfilled, (state, { payload }) => {
        state.stripe_dispute = payload;
      });
    builder.addDefaultCase((state) => state);
  },
});

export const disputeSelectors = disputeAdapter.getSelectors(
  (state) => state.dispute
);

export const disputeByIdSelector = (state, id) => ({
  data: disputeSelectors.selectById(state, id),
  isLoading: state.dispute.detailsIsLoading,
  reasons: state.dispute.reasons,
  stripe_dispute: state.dispute.stripe_dispute,
});
export const disputeAllSelector = (state) => ({
  data: disputeSelectors.selectAll(state),
  pagination: state.dispute.pagination,
  isLoading: state.dispute.isLoading,
  params: state.dispute.params,
  searchTerm: state.dispute.search.term,
  searchFilter: state.dispute.search.filter,
});
export const { setParams, setSearchTerm, setSearchFilter, resetSearch } =
  disputeSlice.actions;

export default disputeSlice.reducer;
